<template>
	<BaseCard :is-editable="isEditable" :title="$t('accident_input_data').toString()" :is-toggleable="false" @clickEdit="edit">
		<div class="space-y-4">
			<div>
				<BaseCardRow :value="accidentDate" :label="$t('accident_date')" />

				<BaseCardRow :value="accidentLocation" :label="$t('accident_location')" />

				<BaseCardRow :value="recordedByPolice" :label="$t('recorded_by_police')" />

				<BaseCardRow :value="personInjured" :label="$t('injured_by_accident')" />
			</div>
			<div>
				<BaseCardRow v-if="showOpponentLicensePlate" :value="opponentLicensePlate" :label="$t('opponent_license_plate')" />

				<BaseCardRow v-if="showOpponentPerson" :value="opponentPersonName" :label="$t('opponent_name')" />

				<BaseCardRow
					v-if="showOpponentInsuranceName"
					:value="this.damageAssessorCase.opposite_insurance_name"
					:label="$t('opposite_insurance_name')"
				/>

				<BaseCardRow
					v-if="showOpponentInsuranceNumber"
					:value="this.damageAssessorCase.opposite_insurance_number"
					:label="$t('opposite_insurance_number')"
				/>

				<BaseCardRow
					v-if="showOpponentInsuranceCaseNumber"
					:value="this.damageAssessorCase.opposite_insurance_case_number"
					:label="$t('opposite_insurance_case_number')"
				/>
			</div>
			<div>
				<BaseCardRow v-if="brokerCase?.note" :value="brokerCase.note" :label="$t('note')" />
			</div>
			<div v-if="accidentReport" class="grid grid-cols-2 break-words leading-none mb-2 max-w-full w-full">
				<div class="font-bold flex items-center">{{ $t('accident_document') }}:</div>

				<div class="flex items-center justify-between">
					<div>
						Hinterlegt
					</div>

					<button
						type="button"
						@click="openAccidentDocuments"
						:title="$t('accident_document').toString()"
						class="bg-sp btn block btn-overview-edit document-action-btn flex items-center justify-center text-white"
					>
						<font-awesome-icon :icon="['fas', 'file-alt']" />
					</button>
				</div>
			</div>
		</div>
	</BaseCard>
</template>

<script>
import BaseCard from '@/components/damagecases/forms/BaseCard';
import BaseCardRow from '@/components/damagecases/forms/BaseCardRow';

import { getGermanDate } from '@/plugins/Utils';

export default {
	name: 'DamageAssessorCaseAccidentCard',

	components: {
		BaseCard,
		BaseCardRow,
	},

	props: {
		damageAssessorCase: {
			type: Object,
			required: true,
		},

		isEditable: {
			type: Boolean,
			default: true,
		},
	},

	computed: {
		brokerCase() {
			return this.damageAssessorCase?.broker_case ?? null;
		},

		accidentTimestamp() {
			return this.damageAssessorCase?.date_of_accident ?? this.brokerCase?.date_of_accident ?? null;
		},

		damageTimeSetByUser() {
			return this.damageAssessorCase?.damage_time_set_by_user ?? this.brokerCase?.damage_time_set_by_user ?? false;
		},

		accidentDate() {
			if (!this.accidentTimestamp) {
				return '-';
			}

			const date = new Date(this.accidentTimestamp * 1000);
			const result = getGermanDate(date);

			if (!this.damageTimeSetByUser) {
				return result;
			}

			const hours = String(date.getHours()).padStart(2, '0');
			const minutes = String(date.getMinutes()).padStart(2, '0');

			return `${result} - ${hours}:${minutes} Uhr`;
		},

		accidentLocation() {
			return this.damageAssessorCase?.accident_location ?? this.brokerCase?.accident_location ?? '-';
		},

		recordedByPolice() {
			const value = this.damageAssessorCase?.is_recorded_by_police ?? this.brokerCase?.is_recorded_by_police;

			if (value === null || value === undefined) {
				return '-';
			}

			return value ? this.$t('general.yes') : this.$t('general.no');
		},

		personInjured() {
			const value = this.damageAssessorCase?.is_person_injured ?? this.brokerCase?.is_person_injured;

			if (value === null || value === undefined) {
				return '-';
			}

			return value ? this.$t('general.yes') : this.$t('general.no');
		},

		opponentPerson() {
			return this.damageAssessorCase?.opposite_person ?? null;
		},

		opponentPersonName() {
			if (!this.opponentPerson) {
				return '-';
			}

			return (
				[this.opponentPerson.firstName ?? this.opponentPerson.first_name, this.opponentPerson.lastName ?? this.opponentPerson.last_name]
					.filter(Boolean)
					.join(' ') || '-'
			);
		},

		opponentLicensePlate() {
			return this.damageAssessorCase?.causer_license_plate ?? this.brokerCase?.causer_license_plate ?? '-';
		},

		showOpponentPerson() {
			return !!this.opponentPersonName && this.opponentPersonName !== '-';
		},

		showOpponentInsuranceName() {
			return !!this.damageAssessorCase?.opposite_insurance_name !== '-';
		},
		showOpponentInsuranceNumber() {
			return !!this.damageAssessorCase?.opposite_insurance_number !== '-';
		},
		showOpponentInsuranceCaseNumber() {
			return !!this.damageAssessorCase?.opposite_insurance_case_number !== '-';
		},
		showOpponentLicensePlate() {
			return !!this.damageAssessorCase?.causer_license_plate || !!this.brokerCase?.causer_license_plate;
		},

		damageAssessorAccidentReport() {
			const media = this.damageAssessorCase?.media ?? [];

			return media.find(item => item.category === 'accident_report') ?? null;
		},

		brokerAccidentReport() {
			const media = this.brokerCase?.media ?? [];

			return media.find(item => item.category === 'accident_report') ?? null;
		},

		accidentReport() {
			return this.damageAssessorAccidentReport ?? this.brokerAccidentReport ?? null;
		},
	},

	methods: {
		edit() {
			if (!this.isEditable) {
				return;
			}

			this.$router.push({
				name: 'damageAssessorCaseEditAccident',

				params: {
					id: this.damageAssessorCase.id,
				},
			});
		},

		openAccidentDocuments() {
			this.$emit('openDocuments', 'customer');
		},
	},
};
</script>

<style scoped>
.document-action-btn {
	width: 32px;
	height: 32px;
	min-width: 32px;
	padding: 0;
	font-size: 0.75rem;
}
</style>
